import { useCallback, useEffect, useRef, useState } from 'react';

import {
  api,
  ApiError,
  type AssistantProposal,
  type AssistantStatus,
} from '../lib/api';

/** The assistant panel.
 *
 *  Absent, not disabled, when no model is configured: `status.available` is false
 *  and this renders nothing at all. The same choice the API makes by answering
 *  404 rather than 503 — a feature nobody configured does not exist, and a
 *  greyed-out button invites a support ticket about a thing that was never
 *  switched on.
 *
 *  Two things here are load-bearing rather than decorative:
 *
 *  **The transcript lives in this component.** Nothing is stored server-side, so
 *  closing the panel ends the conversation. That is deliberate — a chat about
 *  somebody's mail log should not outlive the tab — and it is why every turn
 *  posts the history back.
 *
 *  **A proposal is a card with a button, never an accomplished fact.** The
 *  assistant cannot send mail or suppress an address; it can only draw one up.
 *  The click is the authorization. Anything that reads like "I've suppressed
 *  that" without a click having happened is a bug worth chasing.
 */

type Bubble = {
  who: 'you' | 'assistant';
  text: string;
  tools?: string[];
  proposals?: AssistantProposal[];
  /** Set once a proposal on this bubble has been acted on, so the card cannot be
   *  clicked twice and reads as settled. */
  settled?: string;
};

const SUGGESTIONS = [
  'Why is my domain not verified?',
  'How much of my plan have I used?',
  'Are my bounce rates a problem?',
  'Show me recent failed emails',
];

export function Assistant() {
  const [status, setStatus] = useState<AssistantStatus | null>(null);
  const [open, setOpen] = useState(false);
  const [bubbles, setBubbles] = useState<Bubble[]>([]);
  const [history, setHistory] = useState<Record<string, unknown>[]>([]);
  const [draft, setDraft] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const endRef = useRef<HTMLDivElement | null>(null);
  const inputRef = useRef<HTMLInputElement | null>(null);

  useEffect(() => {
    let live = true;
    void (async () => {
      try {
        const result = await api.assistant.status();
        if (live) setStatus(result);
      } catch {
        // A failure here means no panel. Not worth an error message: the user did
        // not ask for the assistant, so telling them it is unavailable is noise.
        if (live) setStatus(null);
      }
    })();
    return () => {
      live = false;
    };
  }, []);

  useEffect(() => {
    endRef.current?.scrollIntoView({ block: 'end' });
  }, [bubbles, busy]);

  useEffect(() => {
    if (open) inputRef.current?.focus();
  }, [open]);

  const send = useCallback(
    async (text: string) => {
      const question = text.trim();
      if (!question || busy) return;

      setError(null);
      setDraft('');
      setBubbles((current) => [...current, { who: 'you', text: question }]);
      setBusy(true);

      try {
        const turn = await api.assistant.ask(question, history);
        setHistory((current) => [...current, ...turn.messages]);
        setBubbles((current) => [
          ...current,
          {
            who: 'assistant',
            text: turn.reply,
            tools: turn.tools_used,
            proposals: turn.proposals,
          },
        ]);
      } catch (err) {
        const message =
          err instanceof ApiError
            ? err.code === 'assistant_rate_limited'
              ? 'That is a lot of questions at once — give it a moment.'
              : err.message
            : 'Something went wrong reaching the assistant.';
        setError(message);
      } finally {
        setBusy(false);
      }
    },
    [busy, history],
  );

  async function confirm(index: number, proposal: AssistantProposal) {
    setError(null);
    try {
      await api.assistant.confirm(proposal.action, proposal.arguments);
      setBubbles((current) =>
        current.map((bubble, i) =>
          i === index ? { ...bubble, settled: 'done' } : bubble,
        ),
      );
    } catch (err) {
      setError(err instanceof ApiError ? err.message : 'That could not be done.');
    }
  }

  function decline(index: number) {
    setBubbles((current) =>
      current.map((bubble, i) =>
        i === index ? { ...bubble, settled: 'declined' } : bubble,
      ),
    );
  }

  // No model configured: nothing at all, rather than a button that apologises.
  if (!status?.available) return null;

  if (!open) {
    return (
      <button
        type="button"
        className="assistant-launch"
        onClick={() => setOpen(true)}
        aria-expanded={false}
      >
        <span aria-hidden="true">✦</span> Ask
      </button>
    );
  }

  return (
    <aside
      className="assistant-panel"
      role="complementary"
      aria-label="Account assistant"
    >
      <header className="assistant-head">
        <div>
          <strong>Assistant</strong>
          <div className="assistant-sub">
            Answers about this account.{' '}
            {status.self_hosted === false && (
              // Said, not buried. This deployment sends message subjects and
              // recipient addresses to somebody else's server, and the person
              // whose data it is should be able to see that.
              <span className="assistant-warn">Runs on an external provider.</span>
            )}
          </div>
        </div>
        <button
          type="button"
          className="btn btn-sm"
          onClick={() => setOpen(false)}
          aria-label="Close the assistant"
        >
          Close
        </button>
      </header>

      <div className="assistant-log">
        {bubbles.length === 0 && (
          <div className="assistant-empty">
            <p>Ask about domains, deliverability, quota or a specific message.</p>
            <div className="assistant-chips">
              {SUGGESTIONS.map((suggestion) => (
                <button
                  key={suggestion}
                  type="button"
                  className="assistant-chip"
                  onClick={() => void send(suggestion)}
                >
                  {suggestion}
                </button>
              ))}
            </div>
          </div>
        )}

        {bubbles.map((bubble, index) => (
          <div key={index} className={`assistant-bubble ${bubble.who}`}>
            <div className="assistant-text">{bubble.text}</div>

            {bubble.tools && bubble.tools.length > 0 && (
              // Shown because an assistant that says where a number came from is
              // trusted far more than one that simply asserts it.
              <div className="assistant-tools">
                checked: {[...new Set(bubble.tools)].join(', ')}
              </div>
            )}

            {bubble.proposals?.map((proposal) => (
              <div className="assistant-proposal" key={proposal.action}>
                <div className="assistant-proposal-head">{proposal.summary}</div>
                <div className="assistant-proposal-effect">{proposal.effect}</div>

                {bubble.settled === 'done' ? (
                  <div className="assistant-settled">Done.</div>
                ) : bubble.settled === 'declined' ? (
                  <div className="assistant-settled">Not done.</div>
                ) : (
                  <div className="row" style={{ gap: 6, marginTop: 8 }}>
                    <button
                      type="button"
                      className="btn btn-sm btn-primary"
                      onClick={() => void confirm(index, proposal)}
                    >
                      Confirm
                    </button>
                    <button
                      type="button"
                      className="btn btn-sm"
                      onClick={() => decline(index)}
                    >
                      No
                    </button>
                  </div>
                )}
              </div>
            ))}
          </div>
        ))}

        {busy && (
          <div className="assistant-bubble assistant">
            <div className="assistant-text muted">Checking…</div>
          </div>
        )}

        <div ref={endRef} />
      </div>

      {error && (
        <div className="assistant-error" role="status">
          {error}
        </div>
      )}

      <form
        className="assistant-compose"
        onSubmit={(event) => {
          event.preventDefault();
          void send(draft);
        }}
      >
        <label className="sr-only" htmlFor="assistant-input">
          Ask the assistant
        </label>
        <input
          id="assistant-input"
          ref={inputRef}
          type="text"
          value={draft}
          onChange={(event) => setDraft(event.target.value)}
          placeholder="Ask about this account…"
          autoComplete="off"
          disabled={busy}
        />
        <button className="btn btn-sm btn-primary" disabled={busy || !draft.trim()}>
          Ask
        </button>
      </form>

      <p className="assistant-foot">
        {/* Both facts belong here rather than in a tooltip: one sets expectations
            about accuracy, the other about privacy. */}
        Answers come from your account data and can be wrong — check anything that
        matters. Nothing said here is stored.
      </p>
    </aside>
  );
}
